/**
 * @file crm-list-query.ts
 * @description Общие кирпичики состояния реестров зоны «Клиенты и сделки» в
 * адресе страницы (phase19.31): размеры страницы, разбор чисел из адреса,
 * границы страницы «1–50 из 312», сброс страницы при смене фильтра.
 *
 * Реестры сделок и клиентов держат фильтры и страницу в адресе (как реестр
 * заказов): F5 и «назад» из карточки возвращают тот же отбор и ту же
 * страницу. Значения по умолчанию в адрес не пишутся.
 */

import { positiveInt } from "./list-query";

/** Размеры страницы: 50 — по умолчанию, 25 и 100 — на выбор. */
export const CRM_PAGE_SIZES = [25, 50, 100] as const;
export const CRM_DEFAULT_PAGE_SIZE = 50;

/** Целое ≥ 1 из строки адреса — общее для всех реестров (`list-query.ts`). */
export { positiveInt };

/** Размер страницы из адреса: только из списка допустимых. */
export function pageSizeFromParam(raw: string | null): number {
  const size = positiveInt(raw, CRM_DEFAULT_PAGE_SIZE);
  return (CRM_PAGE_SIZES as readonly number[]).includes(size) ? size : CRM_DEFAULT_PAGE_SIZE;
}

/**
 * Поменять часть состояния. Смена любого фильтра или размера страницы
 * возвращает на первую страницу: третьей страницы старого отбора в новом
 * отборе может не быть.
 */
export function patchListQuery<Q extends { page: number }>(query: Q, patch: Partial<Q>): Q {
  const keepPage = Object.keys(patch).every((key) => key === "page");
  return { ...query, ...patch, page: keepPage ? (patch.page ?? query.page) : 1 };
}

/** «1–50 из 312»: границы текущей страницы и число страниц. */
export function listPageRange(page: number, pageSize: number, total: number) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(total, page * pageSize);
  return { from, to, pages };
}

/** Страница → `offset/limit` запроса к серверу. */
export function pageToOffset(page: number, pageSize: number): { limit: number; offset: number } {
  return { limit: pageSize, offset: (page - 1) * pageSize };
}
